<template>
  <div class="rejected-loads-selector">
    <label class="block text-sm font-medium mb-2">
      <i class="fas fa-ban text-red-600 mr-1"></i>
      Kabul Edilmeyen Yükler
      <span class="text-xs text-gray-500 ml-2">(Müşterinin kabul etmediği ürünler)</span>
    </label>

    <!-- Selected Products List -->
    <div v-if="selectedProducts.length > 0" class="mb-3">
      <div class="border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-800">
        <div
          v-for="(productName, index) in selectedProducts"
          :key="index"
          class="flex items-center justify-between mb-2 p-2 bg-white dark:bg-gray-700 rounded border border-red-200 dark:border-red-800"
        >
          <div class="flex-1">
            <span class="text-red-600 dark:text-red-400">
              <i class="fas fa-box mr-1"></i>
              {{ productName }}
            </span>
          </div>
          <button
            @click="removeProduct(index)"
            type="button"
            class="ml-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add Product Dropdown -->
    <div class="relative">
      <button
        @click="showDropdown = !showDropdown"
        type="button"
        class="w-full px-3 py-2 text-left border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-gray-200"
      >
        <span v-if="selectedProducts.length === 0" class="text-gray-500 dark:text-gray-400">
          Ürün seçin...
        </span>
        <span v-else>
          {{ selectedProducts.length }} ürün seçildi
        </span>
        <i class="fas fa-chevron-down float-right mt-1"></i>
      </button>

      <!-- Dropdown -->
      <div
        v-if="showDropdown"
        @click.stop
        class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
      >
        <!-- Search -->
        <div class="p-2 border-b border-gray-200 dark:border-gray-600">
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Ürün ara..."
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-gray-200"
            @input="filterProducts"
          >
        </div>

        <!-- Loading -->
        <div v-if="loading" class="p-4 text-center dark:text-gray-200">
          <i class="fas fa-spinner fa-spin"></i> Yükleniyor...
        </div>

        <!-- Product List -->
        <div v-else class="max-h-64 overflow-y-auto">
          <div v-if="getFilteredProducts().length === 0" class="p-4 text-center text-gray-500 dark:text-gray-400">
            Ürün bulunamadı
          </div>
          <div
            v-else
            v-for="product in getFilteredProducts()"
            :key="product.id"
            @click="toggleProduct(product)"
            class="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center justify-between"
          >
            <div class="flex-1">
              <div class="font-medium dark:text-gray-200">
                <i class="fas fa-box mr-1"></i>
                {{ product.name }}
              </div>
              <div v-if="product.chemicalName" class="text-xs text-gray-600 dark:text-gray-400">
                {{ product.chemicalName }}
              </div>
            </div>
            <div v-if="isProductSelected(product.name)" class="text-green-600 dark:text-green-400">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Text -->
    <p class="mt-2 text-xs text-gray-600 dark:text-gray-400">
      <i class="fas fa-info-circle mr-1"></i>
      Müşterinin kabul etmediği ürünleri seçin. Araç öneri sisteminde bu ürünleri son yük olarak taşıyan araçlar uyarı ile gösterilecektir.
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import { product } from '../../../../networking/urlmanager';

export default {
  name: 'RejectedLoadsSelector',
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showDropdown: false,
      searchTerm: '',
      loading: false,
      allProducts: [],
      selectedProducts: []
    };
  },
  watch: {
    value: {
      handler(newVal) {
        console.log('rejectedLoadsSelector - value changed:', newVal, 'type:', typeof newVal);

        if (!newVal) {
          this.selectedProducts = [];
          return;
        }

        // String ise parse et
        if (typeof newVal === 'string') {
          try {
            const parsed = JSON.parse(newVal);
            this.selectedProducts = Array.isArray(parsed) ? parsed : [];
            console.log('Parsed from string:', this.selectedProducts);
          } catch (e) {
            console.error('Rejected loads parse error:', e);
            this.selectedProducts = [];
          }
        } else if (Array.isArray(newVal)) {
          this.selectedProducts = [...newVal];
          console.log('Set from array:', this.selectedProducts);
        } else {
          console.warn('Unexpected value type:', typeof newVal);
          this.selectedProducts = [];
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.loadProducts();
    // Escape tuşuna basıldığında dropdown'u kapat
    document.addEventListener('keydown', this.handleEscape);
    // Click outside to close
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async loadProducts() {
      this.loading = true;
      try {
        const response = await axios.get(product.getAllNotPagination, {
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          }
        });

        console.log('Products API response:', response.data);

        if (response.data.code === 200) {
          // API direkt array döndürüyor, detail property'si yok
          this.allProducts = Array.isArray(response.data.data)
            ? response.data.data
            : (response.data.data.detail || []);

          console.log('Loaded products count:', this.allProducts.length);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        this.$toast?.error?.('Ürün listesi yüklenirken hata oluştu');
      } finally {
        this.loading = false;
      }
    },

    filterProducts() {
      // getFilteredProducts metodu zaten filtreleme yapıyor
      // Bu sadece trigger için
    },

    getFilteredProducts() {
      if (!this.searchTerm) {
        return this.allProducts;
      }

      const search = this.searchTerm.toLowerCase();
      return this.allProducts.filter(product =>
        product.name?.toLowerCase().includes(search) ||
        product.chemicalName?.toLowerCase().includes(search)
      );
    },

    toggleProduct(product) {
      const index = this.selectedProducts.indexOf(product.name);

      if (index > -1) {
        this.removeProduct(index);
      } else {
        this.selectedProducts.push(product.name);
        this.$emit('input', [...this.selectedProducts]);
      }
    },

    removeProduct(index) {
      this.selectedProducts.splice(index, 1);
      this.$emit('input', [...this.selectedProducts]);
    },

    isProductSelected(productName) {
      return this.selectedProducts.includes(productName);
    },

    handleEscape(e) {
      if (e.key === 'Escape' && this.showDropdown) {
        this.showDropdown = false;
      }
    },

    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.showDropdown = false;
      }
    }
  }
};
</script>

<style scoped>
.rejected-loads-selector {
  position: relative;
}
</style>
